// import hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import components
import { TextField } from '@mui/material';
import {
  GridContainer,
  GridCompact12,
  GridCompact3,
  GridCompact6
} from 'components/shared/Grid/Grid.components';
import ControlledDutyCodeSelector from 'components/shared/ControlledFields/ControlledDutyCodeSelector';

// import queries
import { queries } from 'RestAPIs/rapini';

// import utils
import { formatISOString, formatTime } from 'utils/date';

// import types
import type { DutyCode } from 'generated';
import type { Option as DutyCodeOption } from 'components/shared/DutyCodeSelector.types';
import type { OverrideDutyCodeProps } from './OverrideDutyCode.types';

function OverrideDutyCode({
  control,
  disabled,
  onDutyCodeSelect,
  referenceDate
}: OverrideDutyCodeProps) {
  'use memo';

  const { t } = useTranslation('common');
  const [{ validFrom, validTo, periods }, setDutyCode] = useState<
    Omit<DutyCode, 'dutyCodeId'>
  >({
    validFrom: '',
    validTo: '',
    periods: [],
    periodsCanBeEdited: false,
    canBeDeleted: false
  });
  const [overrideDutyCode, setOverrideDutyCode] =
    useState<DutyCodeOption | null>(null);

  const { data: dutyCodes } = queries.useGetDutyCodes(
    referenceDate?.toISOString()
  );

  useEffect(() => {
    if (overrideDutyCode) {
      const selectedDutyCode = dutyCodes?.find(
        ({ dutyCodeId }) => dutyCodeId === overrideDutyCode.dutyCodeId
      );
      if (selectedDutyCode) {
        setDutyCode(selectedDutyCode);
        onDutyCodeSelect(selectedDutyCode);
      }
    } else {
      setDutyCode({
        validFrom: '',
        validTo: '',
        periods: [],
        periodsCanBeEdited: false,
        canBeDeleted: false
      });
      onDutyCodeSelect();
    }
  }, [overrideDutyCode, dutyCodes, onDutyCodeSelect]);

  return (
    <GridContainer mb={overrideDutyCode ? 2.5 : 0}>
      <GridCompact12>
        <ControlledDutyCodeSelector
          control={control}
          name="overrideDutyCodeId"
          onChange={(e, value) => {
            setOverrideDutyCode(value);
          }}
          label={t('overrideDutyCode')}
          fullWidth
          disabled={disabled}
        />
      </GridCompact12>
      <GridCompact6>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label={t('validFrom')}
          value={validFrom ? formatISOString(validFrom) : '-'}
          disabled
        />
      </GridCompact6>
      <GridCompact6>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label={t('validTo')}
          value={validTo ? formatISOString(validTo) : '-'}
          disabled
        />
      </GridCompact6>
      <GridCompact12>
        <GridContainer>
          {periods?.map((period, index) => (
            <>
              <GridCompact3>
                <TextField
                  variant="outlined"
                  size="small"
                  label={index === 0 ? t('startTime') : ''}
                  value={formatTime(period.startTime)}
                  disabled
                  fullWidth
                />
              </GridCompact3>
              <GridCompact3>
                <TextField
                  variant="outlined"
                  size="small"
                  label={index === 0 ? t('endTime') : ''}
                  value={formatTime(period.endTime)}
                  disabled
                  fullWidth
                />
              </GridCompact3>
              <GridCompact6>
                <TextField
                  variant="outlined"
                  size="small"
                  label={index === 0 ? t('dutyHourType') : ''}
                  value={period.dutyHourType?.description || '-'}
                  disabled
                  fullWidth
                />
              </GridCompact6>
            </>
          ))}
        </GridContainer>
      </GridCompact12>
    </GridContainer>
  );
}

export default OverrideDutyCode;
